import { useEffect, useEffectEvent, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import type { ElkExtendedEdge, ElkNode } from "elkjs/lib/elk.bundled.js";
import { getElk } from "@lib/loaders/elkLoader";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import {
    getBundleLabel,
    getEdgeLabel,
    unorderedPairKey,
} from "./ownershipGraph.helpers";
import {
    CANVAS_GRID,
    ENTITY_NODE_HEIGHT,
    ENTITY_NODE_WIDTH,
    OwnershipEntityNode,
    OwnershipFlowEdge,
    OwnershipFlowNode,
    OwnershipGroupNode,
} from "./ownership.types";

const snapToGrid = (value: number): number =>
    Math.round(value / CANVAS_GRID) * CANVAS_GRID;

const GROUP_PADDING = 40;
const GROUP_LAYER_SPACING = 90;
// Floor for same-layer (sibling) spacing. The actual spacing is widened at
// build time to fit the widest reference-edge label routed in a sibling gap —
// labels are user-named and can grow long, so a fixed gap would clip them.
const SIBLING_SPACING_FLOOR = 40;
// Clearance kept on each side of a label sitting in a sibling gap.
const LABEL_GUTTER = 28;
// Rough label-box width estimate (medium 14px Inter + button padding); only
// needs to be close enough to reserve horizontal room.
const LABEL_CHAR_WIDTH = 7.5;
const LABEL_BOX_PADDING = 28;
// Spacing options NOT inherited by compound (group) nodes from the root graph,
// so they are applied to both. `elk.spacing.nodeNode` is injected dynamically
// (see `siblingSpacingFor`).
const BASE_LAYOUT_OPTIONS: Record<string, string> = {
    "elk.algorithm": "layered",
    "elk.direction": "DOWN",
    "elk.layered.spacing.nodeNodeBetweenLayers": "90",
    "elk.spacing.componentComponent": "40",
    "elk.hierarchyHandling": "INCLUDE_CHILDREN",
    "elk.edgeRouting": "ORTHOGONAL",
    "elk.layered.mergeEdges": "false",
    "elk.layered.spacing.edgeNodeBetweenLayers": "20",
    // half of elk.spacing.componentComponent to keep edges close to their nodes
    // BALANCED averages the four Brandes-Köpf alignments, centering
    // parents over their children instead of flushing them to one side.
    "elk.layered.nodePlacement.bk.fixedAlignment": "BALANCED",
};
const BASE_GROUP_LAYOUT_OPTIONS: Record<string, string> = {
    "elk.padding": `[top=${GROUP_PADDING},left=20,bottom=20,right=20]`,
    "elk.layered.spacing.nodeNodeBetweenLayers": `${GROUP_LAYER_SPACING}`,
    "elk.layered.nodePlacement.bk.fixedAlignment": "BALANCED",
};
const TOP_LAYER_OPTIONS: Record<string, string> = {
    "elk.layered.layering.layerConstraint": "FIRST",
};

function estimateLabelWidth(text: string): number {
    return text.length * LABEL_CHAR_WIDTH + LABEL_BOX_PADDING;
}

/**
 * Sibling (same-layer) spacing wide enough to fit the widest label routed in a
 * gap between two siblings. Mirrors the rendered bundling: relationships between
 * a pair (in either direction) collapse to one "{n} relationships" label, so the
 * spacing is sized for what's actually shown, not the raw (possibly long) names.
 */
function siblingSpacingFor(edges: OwnershipFlowEdge[]): number {
    const countByPair = new Map<string, number>();
    const singleLabelByPair = new Map<string, string>();
    for (const edge of edges) {
        if (
            !edge.data ||
            edge.data.relationship.type ===
                ConsolidationEntityRelationshipType.ownership
        ) {
            continue;
        }
        const key = unorderedPairKey(edge.source, edge.target);
        countByPair.set(key, (countByPair.get(key) ?? 0) + 1);
        singleLabelByPair.set(key, getEdgeLabel(edge.data.relationship));
    }

    let widestLabel = 0;
    for (const [key, count] of countByPair) {
        const label =
            count > 1
                ? getBundleLabel(count)
                : (singleLabelByPair.get(key) ?? "");
        widestLabel = Math.max(widestLabel, estimateLabelWidth(label));
    }
    return Math.max(SIBLING_SPACING_FLOOR, widestLabel + LABEL_GUTTER * 2);
}

/**
 * Orders entity nodes oldest-first. `entity.id` is a Postgres auto-increment,
 * so ascending id is creation order — the single place that invariant is
 * relied on for the empty-canvas tiering and the loose-personal-owner anchor.
 */
export function byCreationOrder(
    a: OwnershipEntityNode,
    b: OwnershipEntityNode,
): number {
    return a.data.entity.id - b.data.entity.id;
}

function isPersonalEntityNode(node: OwnershipFlowNode): boolean {
    return node.type === "entityNode" && node.data.entity.isPersonal;
}

function isEntityNode(node: OwnershipFlowNode): node is OwnershipEntityNode {
    return node.type === "entityNode";
}

/**
 * With no relationships on the canvas there are no edges to lay out, so ELK
 * would drop every entity into a single row. To give a useful starting point
 * we synthesize layout-only edges (never rendered) that put the personal
 * owner on top and every business side by side beneath it — no business is
 * singled out as a suggested parent. Businesses keep creation order
 * (ascending id) for a stable left-to-right arrangement.
 */
function buildSuggestedHierarchyEdges(
    rootNodes: OwnershipFlowNode[],
): ElkExtendedEdge[] {
    const entityNodes = rootNodes.filter(isEntityNode);
    const businessNodes = entityNodes
        .filter((node) => !node.data.entity.isPersonal)
        .sort(byCreationOrder);
    const personalNodes = entityNodes
        .filter((node) => node.data.entity.isPersonal)
        .sort(byCreationOrder);

    const personalAnchor = personalNodes[0];
    if (!personalAnchor) {
        return [];
    }
    return businessNodes.map((business) => ({
        id: `suggested:${personalAnchor.id}->${business.id}`,
        sources: [personalAnchor.id],
        targets: [business.id],
    }));
}

/**
 * The node a loose personal owner should sit above: the consolidation group's
 * parent, else the ownership root business, else the oldest business.
 */
function resolveTopAnchorId(
    nodes: OwnershipFlowNode[],
    ownershipEdges: OwnershipFlowEdge[],
): string | null {
    const groupNode = nodes.find(
        (node): node is OwnershipGroupNode => node.type === "groupNode",
    );
    if (groupNode) {
        return groupNode.data.group.parentEntityUuid;
    }
    const businessNodes = nodes
        .filter(isEntityNode)
        .filter((node) => !node.data.entity.isPersonal);
    const ownershipTargets = new Set(ownershipEdges.map((edge) => edge.target));
    const rootBusiness = businessNodes.find(
        (node) =>
            !ownershipTargets.has(node.id) &&
            ownershipEdges.some((edge) => edge.source === node.id),
    );
    if (rootBusiness) {
        return rootBusiness.id;
    }
    return [...businessNodes].sort(byCreationOrder)[0]?.id ?? null;
}

/**
 * A personal owner with no relationship at all would otherwise be laid out as
 * its own component beside the chart. Tie it (layout-only, never rendered) to
 * the top of the hierarchy so the FIRST-layer pin keeps it above the parent /
 * consolidation group instead of off to the side.
 */
function buildLoosePersonalEdges(
    nodes: OwnershipFlowNode[],
    ownershipEdges: OwnershipFlowEdge[],
    allEdges: OwnershipFlowEdge[],
): ElkExtendedEdge[] {
    const anchorId = resolveTopAnchorId(nodes, ownershipEdges);
    if (!anchorId) {
        return [];
    }
    const connectedIds = new Set(
        allEdges.flatMap((edge) => [edge.source, edge.target]),
    );
    return nodes
        .filter(isEntityNode)
        .filter(
            (node) =>
                node.data.entity.isPersonal &&
                !connectedIds.has(node.id) &&
                node.id !== anchorId,
        )
        .map((node) => ({
            id: `loose-personal:${node.id}`,
            sources: [node.id],
            targets: [anchorId],
        }));
}

/**
 * Builds the ELK graph: group nodes become compound nodes containing their
 * member entity nodes, so ELK returns child coordinates relative to the
 * parent — exactly what XYFlow `parentId` expects.
 *
 * Only ownership edges participate in the layout, so the tree mirrors the
 * ownership hierarchy; reference relationships render between the laid-out
 * nodes without distorting layers. Personal owners are pinned to the top
 * layer. Exported for unit tests.
 */
export function buildElkGraph(
    nodes: OwnershipFlowNode[],
    edges: OwnershipFlowEdge[],
): ElkNode {
    const childrenByParent = new Map<string, OwnershipFlowNode[]>();
    const rootNodes: OwnershipFlowNode[] = [];

    for (const node of nodes) {
        if (node.type === "entityNode" && node.parentId) {
            const siblings = childrenByParent.get(node.parentId) ?? [];
            siblings.push(node);
            childrenByParent.set(node.parentId, siblings);
        } else if (node.type === "entityNode") {
            rootNodes.push(node);
        }
    }

    const toElkLeaf = (node: OwnershipFlowNode): ElkNode => ({
        id: node.id,
        width: ENTITY_NODE_WIDTH,
        height: ENTITY_NODE_HEIGHT,
        ...(isPersonalEntityNode(node)
            ? { layoutOptions: TOP_LAYER_OPTIONS }
            : {}),
    });
    const toElkEdge = (edge: OwnershipFlowEdge): ElkExtendedEdge => ({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target],
    });

    const ownershipEdges = edges.filter(
        (edge) =>
            edge.data?.relationship.type ===
            ConsolidationEntityRelationshipType.ownership,
    );
    // ELK requires an edge to be declared on the lowest common ancestor of its
    // endpoints. An edge whose source and target sit inside the same group
    // compound must live on that compound — declaring it at the root leaves
    // intra-group edges unrouted (no sections), so their arrows vanish.
    const parentByEntityId = new Map(
        nodes
            .filter((node) => node.type === "entityNode" && node.parentId)
            .map((node) => [node.id, node.parentId] as const),
    );
    const edgesByGroupId = new Map<string, ElkExtendedEdge[]>();
    const rootEdges: ElkExtendedEdge[] = [];
    for (const edge of ownershipEdges) {
        const sourceParent = parentByEntityId.get(edge.source);
        const targetParent = parentByEntityId.get(edge.target);
        if (sourceParent && sourceParent === targetParent) {
            const groupEdges = edgesByGroupId.get(sourceParent) ?? [];
            groupEdges.push(toElkEdge(edge));
            edgesByGroupId.set(sourceParent, groupEdges);
        } else {
            rootEdges.push(toElkEdge(edge));
        }
    }

    // Widen sibling spacing to fit the longest (user-named) reference label.
    const siblingSpacing = `${siblingSpacingFor(edges)}`;
    const layoutOptions: Record<string, string> = {
        ...BASE_LAYOUT_OPTIONS,
        "elk.spacing.nodeNode": siblingSpacing,
    };
    const groupLayoutOptions: Record<string, string> = {
        ...BASE_GROUP_LAYOUT_OPTIONS,
        "elk.spacing.nodeNode": siblingSpacing,
    };

    const children: ElkNode[] = [
        ...nodes
            .filter((node) => node.type === "groupNode")
            .map((node) => ({
                id: node.id,
                layoutOptions: groupLayoutOptions,
                children: (childrenByParent.get(node.id) ?? []).map(toElkLeaf),
                edges: edgesByGroupId.get(node.id) ?? [],
            })),
        ...rootNodes.map(toElkLeaf),
    ];

    // No real edges means an empty canvas — fall back to a suggested tiered
    // arrangement so entities aren't dumped into a single row. Otherwise keep
    // relationship-less personal owners pinned above the parent / group.
    const layoutEdges =
        edges.length === 0
            ? buildSuggestedHierarchyEdges(rootNodes)
            : [
                  ...rootEdges,
                  ...buildLoosePersonalEdges(nodes, ownershipEdges, edges),
              ];

    return {
        id: "root",
        layoutOptions,
        children,
        edges: layoutEdges,
    };
}

interface NodeLayout {
    x: number;
    y: number;
    width?: number;
    height?: number;
}

interface UseElkLayoutResult {
    nodes: OwnershipFlowNode[];
    isLayingOut: boolean;
}

export function useElkLayout(
    nodes: OwnershipFlowNode[],
    edges: OwnershipFlowEdge[],
): UseElkLayoutResult {
    // Only geometry lives in state; rendered nodes are derived from the
    // CURRENT nodes so data changes (treatment, names) show immediately
    // even when the graph shape — and therefore the layout — is unchanged.
    const [layoutById, setLayoutById] = useState<Map<string, NodeLayout>>(
        new Map(),
    );
    // The graph shape the current geometry was computed for. Until a fresh
    // layout lands, stale positions must not be applied to a changed graph
    // (e.g. after consolidate, nodes gain a parentId and a new group node
    // appears).
    const [layoutGraphKey, setLayoutGraphKey] = useState<string | null>(null);

    const graphKey = useMemo(
        () =>
            JSON.stringify([
                nodes.map(({ id, parentId }) => [id, parentId ?? null]),
                edges.map(({ id, source, target, data }) => [
                    id,
                    source,
                    target,
                    data?.relationship.type,
                ]),
            ]),
        [nodes, edges],
    );

    const startLayout = useEffectEvent((layoutKey: string) => {
        if (nodes.length === 0) {
            setLayoutById(new Map());
            setLayoutGraphKey(layoutKey);
            return;
        }

        let isCancelled = false;

        getElk()
            .then((elk) => elk.layout(buildElkGraph(nodes, edges)))
            .then((layout) => {
                if (isCancelled) {
                    return;
                }
                const nextLayoutById = new Map<string, NodeLayout>();
                const visit = (elkNode: ElkNode) => {
                    for (const child of elkNode.children ?? []) {
                        // Snap ELK's float coordinates onto the canvas grid so
                        // the auto layout already sits where drag would snap it —
                        // otherwise a dragged node can never return to its
                        // (off-grid) starting spot.
                        nextLayoutById.set(child.id, {
                            x: snapToGrid(child.x ?? 0),
                            y: snapToGrid(child.y ?? 0),
                            width: child.width,
                            height: child.height,
                        });
                        visit(child);
                    }
                };
                visit(layout);

                setLayoutById(nextLayoutById);
                setLayoutGraphKey(layoutKey);
            })
            .catch((error: unknown) => {
                // Fall back to unpositioned nodes rather than an empty canvas.
                Sentry.captureException(error);
                if (!isCancelled) {
                    setLayoutById(
                        new Map(nodes.map((node) => [node.id, { x: 0, y: 0 }])),
                    );
                    setLayoutGraphKey(layoutKey);
                }
            });

        return () => {
            isCancelled = true;
        };
    });
    // graphKey captures the layout-relevant shape of nodes/edges
    useEffect(() => startLayout(graphKey), [graphKey]);

    // Geometry from a previous graph shape must not be applied to the current
    // one — doing so orphans new sub-flow children and reuses stale edge
    // points. Hide everything until a layout for this exact graph lands.
    const isLayingOut = nodes.length > 0 && layoutGraphKey !== graphKey;

    const layoutedNodes = useMemo(
        () =>
            isLayingOut
                ? []
                : nodes.flatMap((node): OwnershipFlowNode[] => {
                      const layout = layoutById.get(node.id);
                      if (!layout) {
                          // Not laid out yet (new node while ELK is in
                          // flight) — hidden until the layout lands.
                          return [];
                      }
                      if (node.type === "groupNode") {
                          return [
                              {
                                  ...node,
                                  position: { x: layout.x, y: layout.y },
                                  width: layout.width,
                                  height: layout.height,
                              },
                          ];
                      }
                      return [
                          { ...node, position: { x: layout.x, y: layout.y } },
                      ];
                  }),
        [isLayingOut, nodes, layoutById],
    );

    return { nodes: layoutedNodes, isLayingOut };
}
